import React from 'react'
import i18n from '../i18n'
import { reportError } from '../lib/error-reporter'
import { MAIN_APP_ROUTES } from '@/lib/routes'

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

interface ErrorBoundaryProps {
  children: React.ReactNode
}

/**
 * Global error boundary to catch unhandled React errors.
 * Prevents white screen of death and provides recovery option.
 *
 * Note: Uses i18n.t() directly since class components can't use hooks.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error and send telemetry to backend
    reportError(error, errorInfo.componentStack ?? undefined)
  }

  handleReload = (): void => {
    window.location.reload()
  }

  // Navigate-away escape hatch so a crash on a sub-route never traps the user
  // (frontend/CLAUDE.md error-boundary rule). A class component can't use the
  // router, so this is a hard location change to the home route.
  handleGoHome = (): void => {
    window.location.assign(MAIN_APP_ROUTES.home)
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      // Use i18n.t() directly since we can't use hooks in class components
      const t = i18n.t.bind(i18n)

      return (
        <div className="flex items-center justify-center min-h-screen p-6 bg-neutral-50">
          <div className="max-w-md text-center">
            <div className="mb-6">
              <svg
                className="w-16 h-16 mx-auto text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="mb-3 text-2xl font-semibold text-neutral-900">
              {t('common.error')}
            </h1>
            <p className="mb-6 text-neutral-600">
              {t('errors.unexpectedError')}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={this.handleReload}
                className="px-6 py-3 font-medium text-white transition-colors rounded-lg bg-primary-600 hover:bg-primary-700"
              >
                {t('errors.reloadPage')}
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-6 py-3 font-medium transition-colors rounded-lg text-neutral-700 bg-neutral-100 hover:bg-neutral-200"
              >
                {t('errors.goHome', { defaultValue: 'Go to start page' })}
              </button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <div className="p-4 mt-6 text-left rounded bg-neutral-100">
                <p className="mb-2 font-mono text-sm text-red-600">
                  {this.state.error.message}
                </p>
                <pre className="overflow-auto text-xs text-neutral-500 max-h-32">
                  {this.state.error.stack}
                </pre>
              </div>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
